import { StockDto, PurchaseDto, SaleDto, DividendDto } from '../../../types/stocks';

interface PortfolioPoint {
  date: string; // ISO date (month end)
  value: number; // approximate mark-to-market value
  invested: number; // cost basis of open positions
  cumulativeDividends: number; // dividends received up to this point
  netContribution: number; // purchases cost - sale proceeds during this period
}

interface MonthlyReturnPoint extends PortfolioPoint {
  monthlyReturnPct: number; // time-weighted style approximation
}

export interface PortfolioPerformanceSummary {
  points: MonthlyReturnPoint[];
  totalReturnPct: number;
  annualizedReturnPct: number;
  volatilityAnnualPct: number; // using monthly std dev * sqrt(12)
  sharpeRatio: number; // (annualized - riskFree)/volatility
  maxDrawdownPct: number;
}

function monthEndDates(start: Date, end: Date): Date[] {
  const dates: Date[] = [];
  const cursor = new Date(start.getFullYear(), start.getMonth(), 1);
  while (cursor <= end) {
    const monthEnd = new Date(cursor.getFullYear(), cursor.getMonth() + 1, 0); // last day of month
    dates.push(monthEnd);
    cursor.setMonth(cursor.getMonth() + 1);
  }
  return dates.filter(d => d <= end);
}

function parseDate(s: string): Date { return new Date(s); }

// Approximate price path: linearly interpolate from average cost to current price across holding period
function approximatePrice(stock: StockDto, date: Date): number {
  const shares = stock.purchases.reduce((sum,p)=> sum + (parseDate(p.date) <= date ? p.quantity : 0), 0) -
    stock.sales.reduce((sum,s)=> sum + (parseDate(s.date) <= date ? s.quantity : 0), 0);
  if (shares <= 0) return 0;
  const totalInvested = stock.purchases.reduce((sum,p)=> sum + (parseDate(p.date) <= date ? p.quantity * p.pricePerShare : 0), 0);
  const avgCost = shares > 0 ? totalInvested / shares : 0;
  const earliestPurchaseDate = stock.purchases.length ? stock.purchases.reduce((earliest,p)=> earliest < p.date ? earliest : p.date, stock.purchases[0].date) : null;
  if (!earliestPurchaseDate) return 0;
  const start = parseDate(earliestPurchaseDate);
  const now = new Date();
  const currentPrice = stock.currentPrice ?? avgCost;
  if (date <= start) return avgCost; // before or at first purchase
  const ratio = Math.min(1, Math.max(0, (date.getTime() - start.getTime()) / (now.getTime() - start.getTime())));
  return avgCost + (currentPrice - avgCost) * ratio;
}

function periodCashFlows(purchases: PurchaseDto[], sales: SaleDto[], dividends: DividendDto[], periodStart: Date, periodEnd: Date) {
  const inPeriod = (d: string) => { const dt = parseDate(d); return dt > periodStart && dt <= periodEnd; };
  const purchaseCost = purchases.filter(p=> inPeriod(p.date)).reduce((s,p)=> s + p.quantity * p.pricePerShare, 0);
  const salesProceeds = sales.filter(s=> inPeriod(s.date)).reduce((s,sl)=> s + sl.totalAmount, 0);
  const dividendIncome = dividends.filter(d=> inPeriod(d.date)).reduce((s,dv)=> s + dv.amount, 0);
  return { purchaseCost, salesProceeds, dividendIncome };
}

function buildPortfolioSeries(stocks: StockDto[], start: Date, end: Date = new Date()): PortfolioPoint[] {
  const dates = monthEndDates(start, end);
  const series: PortfolioPoint[] = [];
  // Track invested cost basis cumulatively using deltas per period for accuracy with sales
  let previousInvested = 0;
  let cumulativeDividends = 0;
  let lastDate = new Date(start.getFullYear(), start.getMonth(), 1, 0,0,0,0);
  for (const d of dates) {
    // Compute market value approximation
    let value = 0;
    stocks.forEach(stock => {
      // shares held at date d
      const shares = stock.purchases.reduce((sum,p)=> sum + (parseDate(p.date) <= d ? p.quantity : 0), 0) -
        stock.sales.reduce((sum,s)=> sum + (parseDate(s.date) <= d ? s.quantity : 0), 0);
      if (shares > 0) {
        value += shares * approximatePrice(stock, d);
      }
    });
    // Cash flow deltas in this period
    let periodPurchase = 0, periodSalesProceeds = 0, periodDividends = 0;
    stocks.forEach(stock => {
      const { purchaseCost, salesProceeds, dividendIncome } = periodCashFlows(stock.purchases, stock.sales, stock.dividends, lastDate, d);
      periodPurchase += purchaseCost;
      periodSalesProceeds += salesProceeds;
      periodDividends += dividendIncome;
    });
    cumulativeDividends += periodDividends;
    // Adjust invested: purchases add cost basis, sales reduce cost basis by proportion of average cost (approx). Simpler: subtract sales proceeds * (avg cost ratio). Without exact lot cost we approximate using average cost percentage.
    // We'll approximate cost basis reduction by assuming sale proceeds roughly equals cost basis of shares * (current sale price / cost basis price). This is messy; choose simpler: reduce invested by purchase cost portion: for average price we need sold quantity * average cost so we approximate average cost = (previousInvested / previousMarketValue) * salePrice? Complexity high; choose: treat invested = previousInvested + periodPurchase - (periodSalesProceeds * 0.6) // heuristic.
    // To avoid magic numbers, we instead recompute invested as sum cost basis of currently held shares using purchase history minus average cost per share times sold qty.
    let invested = 0;
    stocks.forEach(stock => {
      // Build running remaining quantities FIFO approximation
      const lots: { qty:number; costPerShare:number }[] = [];
      stock.purchases.filter(p=> parseDate(p.date) <= d).forEach(p=> lots.push({ qty: p.quantity, costPerShare: p.pricePerShare }));
      // Apply sales FIFO
      stock.sales.filter(s=> parseDate(s.date) <= d).forEach(s => {
        let remain = s.quantity;
        while (remain > 0 && lots.length) {
          const lot = lots[0];
            if (lot.qty <= remain) { remain -= lot.qty; lots.shift(); }
            else { lot.qty -= remain; remain = 0; }
        }
      });
      invested += lots.reduce((sum,l)=> sum + l.qty * l.costPerShare, 0);
    });
    const netContribution = periodPurchase - periodSalesProceeds;
    series.push({ date: d.toISOString().substring(0,10), value, invested, cumulativeDividends, netContribution });
    previousInvested = invested;
    lastDate = d;
  }
  return series;
}

function computeMonthlyReturns(series: PortfolioPoint[]): MonthlyReturnPoint[] {
  if (series.length === 0) return [];
  const out: MonthlyReturnPoint[] = [];
  let prev = series[0];
  out.push({ ...prev, monthlyReturnPct: 0 });
  for (let i=1;i<series.length;i++) {
    const cur = series[i];
    const dividendsPeriod = cur.cumulativeDividends - prev.cumulativeDividends;
    const netContribution = cur.netContribution; // already period value
    const base = prev.value + netContribution; // time-weighted style
    const gain = (cur.value + dividendsPeriod) - base;
    const r = base !== 0 ? (gain / base) * 100 : 0;
    out.push({ ...cur, monthlyReturnPct: r });
    prev = cur;
  }
  return out;
}

function summarizePerformance(points: MonthlyReturnPoint[], riskFreeAnnualPct = 2): PortfolioPerformanceSummary {
  if (points.length === 0) {
    return { points: [], totalReturnPct: 0, annualizedReturnPct: 0, volatilityAnnualPct: 0, sharpeRatio: 0, maxDrawdownPct: 0 };
  }
  const first = points[0];
  const last = points[points.length-1];
  const totalReturnPct = first.invested > 0 ? ((last.value + (last.cumulativeDividends)) - first.invested) / first.invested * 100 : 0;
  const months = points.length - 1;
  const annualizedReturnPct = months > 0 ? (Math.pow(1 + totalReturnPct/100, 12/months) - 1) * 100 : totalReturnPct;
  const monthlyReturns = points.slice(1).map(p=> p.monthlyReturnPct/100);
  const avgMonthly = monthlyReturns.length ? monthlyReturns.reduce((s,r)=>s+r,0)/monthlyReturns.length : 0;
  const variance = monthlyReturns.length ? monthlyReturns.reduce((s,r)=> s + Math.pow(r-avgMonthly,2),0)/monthlyReturns.length : 0;
  const stdMonthly = Math.sqrt(variance);
  const volatilityAnnualPct = stdMonthly * Math.sqrt(12) * 100;
  const riskFreeMonthly = Math.pow(1 + riskFreeAnnualPct/100, 1/12) - 1;
  const sharpeRatio = stdMonthly !== 0 ? (avgMonthly - riskFreeMonthly) / stdMonthly : 0;
  // Max drawdown on equity curve of cumulative returns
  let peak = -Infinity; let maxDD = 0; let cumulative = 1;
  points.slice(1).forEach(p => {
    cumulative *= (1 + (p.monthlyReturnPct/100));
    if (cumulative > peak) peak = cumulative;
    const dd = peak > 0 ? (cumulative - peak) / peak : 0;
    if (dd < maxDD) maxDD = dd;
  });
  const maxDrawdownPct = maxDD * 100;
  return { points, totalReturnPct, annualizedReturnPct, volatilityAnnualPct, sharpeRatio, maxDrawdownPct };
}

export function buildPerformance(stocks: StockDto[], timeframe: '6M' | '1Y' | '2Y' | '5Y' | 'ALL'): PortfolioPerformanceSummary {
  const now = new Date();
  const start = new Date(now);
  switch(timeframe){
    case '6M': start.setMonth(start.getMonth()-6); break;
    case '1Y': start.setFullYear(start.getFullYear()-1); break;
    case '2Y': start.setFullYear(start.getFullYear()-2); break;
    case '5Y': start.setFullYear(start.getFullYear()-5); break;
    case 'ALL': {
      // earliest purchase across stocks
      const earliest = stocks.flatMap(s=> s.purchases.map(p=> p.date));
      if (earliest.length){
        const min = earliest.reduce((a,b)=> a < b ? a : b);
        return summarizePerformance(computeMonthlyReturns(buildPortfolioSeries(stocks, new Date(min), now)));
      }
      break;
    }
  }
  return summarizePerformance(computeMonthlyReturns(buildPortfolioSeries(stocks, start, now)));
}
